import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AlbumContext = createContext(null);

const STORAGE_KEY = 'travel_album_draft';

export const useAlbum = () => {
  const context = useContext(AlbumContext);
  if (!context) {
    throw new Error('useAlbum must be used within AlbumProvider');
  }
  return context;
};

export const AlbumProvider = ({ children }) => {
  // 앱 상태
  const [step, setStep] = useState(1);
  const [projectId, setProjectId] = useState(null);
  const [albumName, setAlbumName] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [videoStatus, setVideoStatus] = useState(null);

  // 네비게이션 탭
  const [activeTab, setActiveTab] = useState(0);

  // 저장된 앨범 목록
  const [savedAlbums, setSavedAlbums] = useState([]);

  // 초기 로드시 저장된 앨범 목록 불러오기
  useEffect(() => {
    loadSavedAlbumsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 저장된 앨범 목록 불러오기
  const loadSavedAlbumsList = useCallback(() => {
    try {
      const saved = localStorage.getItem('travel_album_list');
      if (saved) {
        setSavedAlbums(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Failed to load saved albums list:', e);
    }
  }, []);

  // 메시지 설정 헬퍼
  const showMessage = useCallback((text, type = 'success') => {
    setMessage({ text, type });
  }, []);

  // 메시지 초기화
  const clearMessage = useCallback(() => {
    setMessage({ text: '', type: '' });
  }, []);

  // 앱 리셋
  const resetApp = useCallback(() => {
    setStep(1);
    setProjectId(null);
    setAlbumName('');
    setUploadedFiles([]);
    setGroups([]);
    setLoading(false);
    setMessage({ text: '', type: '' });
    setVideoStatus(null);
  }, []);

  // 다음 단계로 이동
  const nextStep = useCallback(() => {
    setStep((prev) => Math.min(prev + 1, 4));
  }, []);

  // 이전 단계로 이동
  const prevStep = useCallback(() => {
    setStep((prev) => Math.max(prev - 1, 1));
  }, []);

  // 특정 단계로 이동
  const goToStep = useCallback((stepNumber) => {
    if (stepNumber >= 1 && stepNumber <= 4) {
      setStep(stepNumber);
    }
  }, []);

  // 그룹 업데이트
  const updateGroup = useCallback((groupId, field, value) => {
    setGroups((prevGroups) =>
      prevGroups.map((g) =>
        g.group_id === groupId ? { ...g, [field]: value } : g
      )
    );
  }, []);

  // 앨범 저장
  const saveAlbum = useCallback((customName) => {
    try {
      const saveName = customName || albumName || `앨범 ${new Date().toLocaleDateString('ko-KR')}`;
      const albumData = {
        id: `album_${Date.now()}`,
        name: saveName,
        savedAt: new Date().toISOString(),
        step,
        projectId,
        albumName: saveName,
        uploadedFiles,
        groups,
        videoStatus,
      };

      // 개별 앨범 저장
      localStorage.setItem(`${STORAGE_KEY}_${albumData.id}`, JSON.stringify(albumData));

      // 앨범 목록 업데이트
      const updatedList = [
        { id: albumData.id, name: albumData.name, savedAt: albumData.savedAt },
        ...savedAlbums.filter(a => a.id !== albumData.id),
      ];
      localStorage.setItem('travel_album_list', JSON.stringify(updatedList));
      setSavedAlbums(updatedList);

      showMessage(`"${albumData.name}" 저장됨`);
      return albumData.id;
    } catch (e) {
      showMessage('저장 실패: ' + e.message, 'error');
      return null;
    }
  }, [step, projectId, albumName, uploadedFiles, groups, videoStatus, savedAlbums, showMessage]);

  // 앨범 불러오기
  const loadAlbum = useCallback((albumId) => {
    try {
      const saved = localStorage.getItem(`${STORAGE_KEY}_${albumId}`);
      if (saved) {
        const albumData = JSON.parse(saved);
        setStep(albumData.step || 1);
        setProjectId(albumData.projectId);
        setAlbumName(albumData.albumName || albumData.name || '');
        setUploadedFiles(albumData.uploadedFiles || []);
        setGroups(albumData.groups || []);
        setVideoStatus(albumData.videoStatus);
        showMessage(`"${albumData.name}" 불러옴`);
        return true;
      }
      showMessage('저장된 앨범을 찾을 수 없습니다', 'error');
      return false;
    } catch (e) {
      showMessage('불러오기 실패: ' + e.message, 'error');
      return false;
    }
  }, [showMessage]);

  // 저장된 앨범 삭제
  const deleteAlbum = useCallback((albumId) => {
    try {
      localStorage.removeItem(`${STORAGE_KEY}_${albumId}`);
      const updatedList = savedAlbums.filter(a => a.id !== albumId);
      localStorage.setItem('travel_album_list', JSON.stringify(updatedList));
      setSavedAlbums(updatedList);
      showMessage('앨범 삭제됨');
    } catch (e) {
      showMessage('삭제 실패: ' + e.message, 'error');
    }
  }, [savedAlbums, showMessage]);

  // 그룹 합치기
  const mergeGroups = useCallback((groupId1, groupId2) => {
    setGroups((prevGroups) => {
      const group1 = prevGroups.find(g => g.group_id === groupId1);
      const group2 = prevGroups.find(g => g.group_id === groupId2);

      if (!group1 || !group2) return prevGroups;

      // 두 그룹 합치기
      const mergedGroup = {
        ...group1,
        num_photos: (group1.num_photos || 0) + (group2.num_photos || 0),
        photos: [...(group1.photos || []), ...(group2.photos || [])],
        first_datetime: group1.first_datetime < group2.first_datetime
          ? group1.first_datetime : group2.first_datetime,
        last_datetime: group1.last_datetime > group2.last_datetime
          ? group1.last_datetime : group2.last_datetime,
        nearby_places: [
          ...(group1.nearby_places || []),
          ...(group2.nearby_places || []).filter(
            p2 => !(group1.nearby_places || []).some(p1 => p1.name === p2.name)
          ),
        ],
      };

      // group2 제거하고 group1 업데이트
      return prevGroups
        .filter(g => g.group_id !== groupId2)
        .map(g => g.group_id === groupId1 ? mergedGroup : g);
    });
    showMessage('그룹이 합쳐졌습니다');
  }, [showMessage]);

  // 그룹 분리 (특정 사진부터 분리)
  const splitGroup = useCallback((groupId, splitAtIndex) => {
    setGroups((prevGroups) => {
      const group = prevGroups.find(g => g.group_id === groupId);
      if (!group || !group.photos || splitAtIndex <= 0 || splitAtIndex >= group.photos.length) {
        return prevGroups;
      }

      const photos1 = group.photos.slice(0, splitAtIndex);
      const photos2 = group.photos.slice(splitAtIndex);

      const newGroupId = Math.max(...prevGroups.map(g => g.group_id)) + 1;

      const group1 = {
        ...group,
        num_photos: photos1.length,
        photos: photos1,
        last_datetime: photos1[photos1.length - 1]?.datetime || group.first_datetime,
      };

      const group2 = {
        ...group,
        group_id: newGroupId,
        num_photos: photos2.length,
        photos: photos2,
        first_datetime: photos2[0]?.datetime || group.last_datetime,
        location: '',
        activity: '',
      };

      const groupIndex = prevGroups.findIndex(g => g.group_id === groupId);
      const newGroups = [...prevGroups];
      newGroups.splice(groupIndex, 1, group1, group2);

      return newGroups;
    });
    showMessage('그룹이 분리되었습니다');
  }, [showMessage]);

  const value = {
    // 상태
    step,
    projectId,
    albumName,
    uploadedFiles,
    groups,
    loading,
    message,
    videoStatus,
    activeTab,
    savedAlbums,

    // 상태 설정자
    setStep,
    setProjectId,
    setAlbumName,
    setUploadedFiles,
    setGroups,
    setLoading,
    setVideoStatus,
    setActiveTab,

    // 헬퍼 함수
    showMessage,
    clearMessage,
    resetApp,
    nextStep,
    prevStep,
    goToStep,
    updateGroup,

    // 앨범 저장/불러오기
    saveAlbum,
    loadAlbum,
    deleteAlbum,
    loadSavedAlbumsList,

    // 그룹 관리
    mergeGroups,
    splitGroup,
  };

  return (
    <AlbumContext.Provider value={value}>
      {children}
    </AlbumContext.Provider>
  );
};

export default AlbumContext;
